import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchActiveBackgroundProcesses,
  fetchBulkActiveBackgroundProcesses,
  BackgroundProcess,
  BackgroundProcessResponse,
} from "apiClients/backgroundProcess";
import { STALE_TIME } from "utils/constants";

export interface ProcessStatus {
  processId: string | number;
  patientId: string;
  name: string;
  state: string;
  currentStep: string;
  error?: string | null;
  createdAt: string;
  isActive: boolean;
}

export interface PatientMonitorData {
  startTime: number;
  isActive: boolean;
}

export type CompletionCallback = () => void;

export interface BackgroundProcessContextType {
  isMonitoring: boolean;
  currentPatientId: string | null;
  monitoredPatients: Map<string, PatientMonitorData>;
  backgroundProcesses: BackgroundProcessResponse | undefined;
  startMonitoring: (patientId: string | number, completionCallback?: CompletionCallback | null) => void;
  stopMonitoring: (patientId?: string | number) => void;
  getCurrentProcessStatus: () => ProcessStatus | null;
  getAllActiveProcesses: () => ProcessStatus[];
  getActiveProcessCount: () => number;
  hasActiveProcesses: () => boolean;
  refetchBackgroundProcesses: () => Promise<any>;
  isRefreshInProgress: (patientId: string | number) => boolean;
  markRefreshInProgress: (patientId: string | number) => void;
  clearRefreshInProgress: (patientId: string | number) => void;
  isPatientBeingMonitored: (patientId: string | number) => boolean;
  checkOnce: (patientId: string | number) => Promise<BackgroundProcess[] | null>;
}

const BackgroundProcessContext = createContext<BackgroundProcessContextType | undefined>(undefined);

export const useBackgroundProcess = (): BackgroundProcessContextType => {
  const context = useContext(BackgroundProcessContext);
  if (!context) {
    throw new Error("useBackgroundProcess must be used within a BackgroundProcessProvider");
  }
  return context;
};

export interface BackgroundProcessProviderProps {
  children: ReactNode;
}

export const BackgroundProcessProvider: React.FC<BackgroundProcessProviderProps> = ({ children }) => {
  const [monitoredPatients, setMonitoredPatients] = useState<Map<string, PatientMonitorData>>(new Map());
  const [isMonitoring, setIsMonitoring] = useState<boolean>(false);
  const [lastCompletedProcesses, setLastCompletedProcesses] = useState<Map<string, Set<string | number>>>(new Map());
  const [completionCallbacks, setCompletionCallbacks] = useState<Map<string, CompletionCallback[]>>(new Map());
  const [activeRefreshRequests, setActiveRefreshRequests] = useState<Map<string, number>>(new Map());

  const currentPatientId = monitoredPatients.size > 0 ? Array.from(monitoredPatients.keys())[0] : null;

  const monitoredPatientIds = Array.from(monitoredPatients.keys()).sort();

  const {
    data: backgroundProcesses,
    refetch: refetchBackgroundProcesses,
    status,
  } = useQuery({
    queryKey: ["globalBackgroundProcesses", monitoredPatientIds],
    queryFn: async () => {
      // Use bulk API to fetch all processes in a single request
      const data = await fetchBulkActiveBackgroundProcesses(monitoredPatientIds);
      return data; // Already in format { count: number, results: Array }
    },
    enabled: isMonitoring && monitoredPatientIds.length > 0,
    refetchInterval: false, // Disabled - using WebSocket instead of polling
    refetchIntervalInBackground: false,
    // Ensure React Query properly deduplicates requests
    staleTime: 0,
    gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
    // Prevent multiple subscriptions from different components
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Force proper deduplication
    networkMode: "always",
  });

  const checkForSmartCompletion = useCallback(() => {
    if (!backgroundProcesses?.results) return;

    const relevantProcesses = backgroundProcesses.results.filter(
      (process) => process.name.includes("Smart Summary") || process.name.includes("Patient Data") || process.name.includes("Refresh")
    );

    monitoredPatients.forEach((patientData, patientId) => {
      if (Date.now() - patientData.startTime > STALE_TIME / 2) {
        stopMonitoring(patientId);
        return;
      }

      const patientProcesses = relevantProcesses.filter((process) => process.patient_id === parseInt(patientId));

      const activeProcesses = patientProcesses.filter((process) => {
        const latestStatus = process.history && process.history.length > 0 ? process.history[process.history.length - 1].status : null;

        return (process.state === "NOT_STARTED" || process.state === "RUNNING") && latestStatus !== "Refresh complete";
      });

      // Only stop monitoring if no active processes AND we've been monitoring for at least 10 seconds
      // This prevents premature termination when background processes haven't been created yet
      const monitoringDuration = Date.now() - patientData.startTime;
      if (activeProcesses.length === 0 && monitoringDuration > 10000) {
        stopMonitoring(patientId);
        return;
      }

      const currentCompletedProcesses = lastCompletedProcesses.get(patientId) || new Set<string | number>();
      const newCompletedProcesses = new Set(currentCompletedProcesses);

      patientProcesses.forEach((process) => {
        const latestStatus = process.history && process.history.length > 0 ? process.history[process.history.length - 1].status : null;

        if (process.state === "DONE" || process.state === "ERROR" || latestStatus === "Refresh complete") {
          newCompletedProcesses.add(process.id);
        }
      });

      const hasNewCompletions = Array.from(newCompletedProcesses).some((id) => !currentCompletedProcesses.has(id));

      if (hasNewCompletions) {
        setLastCompletedProcesses((prev) => {
          const newMap = new Map(prev);
          newMap.set(patientId, newCompletedProcesses);
          return newMap;
        });

        setTimeout(() => {
          const currentActiveProcesses = patientProcesses.filter((p) => p.is_active);
          if (currentActiveProcesses.length === 0) {
            stopMonitoring(patientId);
          }
        }, 2000);
      }
    });
  }, [backgroundProcesses, monitoredPatients, lastCompletedProcesses]);

  useEffect(() => {
    if (status === "success") {
      if (isMonitoring && backgroundProcesses?.results) {
        checkForSmartCompletion();
      }
    }
  }, [status, backgroundProcesses, isMonitoring, checkForSmartCompletion]);

  const getAllActiveProcesses = useCallback((): ProcessStatus[] => {
    if (!backgroundProcesses?.results || backgroundProcesses.results.length === 0) {
      return [];
    }

    const activeProcesses = backgroundProcesses.results.filter(
      (process) =>
        process.is_active &&
        (process.name.includes("Smart Summary") || process.name.includes("Patient Data") || process.name.includes("Refresh"))
    );

    return activeProcesses.map((process) => {
      const latestStep = process.history && process.history.length > 0 ? process.history[process.history.length - 1] : null;

      return {
        processId: process.id,
        patientId: String(process.patient_id),
        name: process.name,
        state: process.state,
        currentStep: latestStep?.status || "Starting...",
        error: process.error,
        createdAt: process.created_at,
        isActive: process.is_active,
      };
    });
  }, [backgroundProcesses]);

  const getCurrentProcessStatus = useCallback((): ProcessStatus | null => {
    const activeProcesses = getAllActiveProcesses();
    return activeProcesses.length > 0 ? activeProcesses[0] : null;
  }, [getAllActiveProcesses]);

  const getActiveProcessCount = useCallback((): number => {
    const activeProcesses = getAllActiveProcesses();
    const uniquePatientIds = new Set(activeProcesses.map((process) => process.patientId));

    return uniquePatientIds.size;
  }, [getAllActiveProcesses]);

  const isRefreshInProgress = useCallback(
    (patientId: string | number): boolean => {
      return activeRefreshRequests.has(String(patientId));
    },
    [activeRefreshRequests]
  );

  const markRefreshInProgress = useCallback((patientId: string | number): void => {
    setActiveRefreshRequests((prev) => {
      const newMap = new Map(prev);
      newMap.set(String(patientId), Date.now());
      return newMap;
    });
  }, []);

  const clearRefreshInProgress = useCallback((patientId: string | number): void => {
    setActiveRefreshRequests((prev) => {
      const newMap = new Map(prev);
      newMap.delete(String(patientId));
      return newMap;
    });
  }, []);

  const isPatientBeingMonitored = useCallback(
    (patientId: string | number): boolean => {
      const patientIdStr = String(patientId);
      const isMonitored = monitoredPatients.has(patientIdStr);
      return isMonitored;
    },
    [monitoredPatients]
  );

  const startMonitoring = useCallback((patientId: string | number, completionCallback: CompletionCallback | null = null): void => {
    if (patientId) {
      const patientIdStr = String(patientId);

      setMonitoredPatients((prev) => {
        const isAlreadyMonitored = prev.has(patientIdStr);
        const newMap = new Map(prev);
        newMap.set(patientIdStr, {
          startTime: isAlreadyMonitored ? prev.get(patientIdStr)?.startTime || Date.now() : Date.now(),
          isActive: true,
        });
        return newMap;
      });

      setLastCompletedProcesses((prev) => {
        const newMap = new Map(prev);
        if (!newMap.has(patientIdStr)) {
          newMap.set(patientIdStr, new Set());
        }
        return newMap;
      });

      if (completionCallback) {
        setCompletionCallbacks((prev) => {
          const newMap = new Map(prev);
          const existingCallbacks = newMap.get(patientIdStr) || [];
          newMap.set(patientIdStr, [...existingCallbacks, completionCallback]);
          return newMap;
        });
      }

      setIsMonitoring(true);
    }
  }, []);

  const stopMonitoring = useCallback(
    (patientId?: string | number): void => {
      if (patientId) {
        const callbacks = completionCallbacks.get(String(patientId)) || [];
        callbacks.forEach((callback) => {
          if (callback && typeof callback === "function") {
            try {
              callback();
            } catch {
              // Ignore errors in callbacks
            }
          }
        });

        setMonitoredPatients((prev) => {
          const newMap = new Map(prev);
          newMap.delete(String(patientId));
          if (newMap.size === 0) {
            setIsMonitoring(false);
          }

          return newMap;
        });

        setLastCompletedProcesses((prev) => {
          const newMap = new Map(prev);
          newMap.delete(String(patientId));
          return newMap;
        });

        setCompletionCallbacks((prev) => {
          const newMap = new Map(prev);
          newMap.delete(String(patientId));
          return newMap;
        });

        clearRefreshInProgress(patientId);
      } else {
        setMonitoredPatients(new Map());
        setLastCompletedProcesses(new Map());
        setCompletionCallbacks(new Map());
        setActiveRefreshRequests(new Map());
        setIsMonitoring(false);
      }
    },
    [completionCallbacks, clearRefreshInProgress]
  );

  const hasActiveProcesses = useCallback((): boolean => {
    const status = getCurrentProcessStatus();
    return status !== null && status.isActive;
  }, [getCurrentProcessStatus]);

  const checkOnce = useCallback(async (patientId: string | number): Promise<BackgroundProcess[] | null> => {
    if (!patientId) return null;

    try {
      const data = await fetchActiveBackgroundProcesses(patientId);
      const activeProcesses =
        data.results?.filter(
          (process) =>
            process.is_active &&
            (process.name.includes("Smart Summary") || process.name.includes("Patient Data") || process.name.includes("Refresh"))
        ) || [];

      return activeProcesses;
    } catch {
      return [];
    }
  }, []);

  const value: BackgroundProcessContextType = {
    isMonitoring,
    currentPatientId,
    monitoredPatients,
    backgroundProcesses,
    startMonitoring,
    stopMonitoring,
    getCurrentProcessStatus,
    getAllActiveProcesses,
    getActiveProcessCount,
    hasActiveProcesses,
    refetchBackgroundProcesses,
    isRefreshInProgress,
    markRefreshInProgress,
    clearRefreshInProgress,
    isPatientBeingMonitored,
    checkOnce,
  };

  return <BackgroundProcessContext.Provider value={value}>{children}</BackgroundProcessContext.Provider>;
};

export default BackgroundProcessContext;
